/**
 * Typed fetch wrapper for the public tracking endpoint.
 *
 * Endpoint: `GET {VITE_SERVICE_MAIL_BASE_URL}/v1/track/:trackingNumber`
 *
 * Contract (mirrored from apps/service-mail):
 *   - 200 → `PublicMailItemView` (PII-stripped projection).
 *   - 404 → no item; we return `null` so callers can render an empty state
 *           without inspecting Error instances.
 *   - 4xx other → throw `ApiError` with the HTTP status (caller renders the
 *           generic error state).
 *   - 5xx / network → throw `ApiError`; React Query's retry takes one swing
 *           at it before propagating to the UI.
 *
 * No auth header is sent — this is the public endpoint. The future global
 * auth middleware on service-mail honours `@Public()` on the controller.
 *
 * Distributed tracing: every call emits a W3C `traceparent` header so the
 * gateway / service-mail can correlate the citizen lookup back to a trace
 * (per architecture/OBSERVABILITY.md). Sampled flag is `01` (sampled) in
 * dev — the upstream sampler decides what actually gets exported.
 */
import { env } from './env';
import {
  type PublicMailItemView,
  isPublicMailItemView,
} from '../features/track/PublicMailItemView';

export class ApiError extends Error {
  readonly status: number;
  /** Optional structured body (Nest BadRequestException-style). */
  readonly body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Generic JSON request helper. Used by the auth + customer API wrappers.
 *
 * - Adds `accept: application/json` and a fresh W3C `traceparent` per call.
 * - Sends `content-type: application/json` only when a body is provided.
 * - Maps non-2xx to `ApiError` (preserves the parsed body if any).
 * - Returns `null` on 204 / no-content responses.
 *
 * 401 path: when the call returns 401 AND a refresh token is in the
 * customer-portal token store, the helper transparently posts to
 * `${VITE_IDENTITY_BASE_URL}/v1/auth/refresh`, persists the rotated pair, and
 * retries ONCE with the fresh bearer. Any failure drops both tokens.
 *
 * `suppressAuthRefresh: true` opts out of the interceptor (used by the login
 * + refresh flows themselves to avoid recursion).
 */
import {
  clearCustomerRefreshToken,
  clearCustomerToken,
  getCustomerRefreshToken,
  setCustomerRefreshToken,
  setCustomerToken,
} from './auth/tokenStore';

export interface RequestJsonOptions {
  readonly method?: string;
  readonly baseUrl: string;
  readonly path: string;
  readonly body?: unknown;
  readonly bearerToken?: string;
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
  /** Internal: set to true on the retried request so we never loop. */
  readonly _isRetry?: boolean;
  /** Skip the 401-refresh-and-retry dance (used by login/refresh flows). */
  readonly suppressAuthRefresh?: boolean;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

let inFlightRefresh: Promise<string | null> | null = null;

async function attemptRefresh(fetchImpl: typeof fetch): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;
  const refreshToken = getCustomerRefreshToken();
  if (!refreshToken) return null;

  inFlightRefresh = (async () => {
    try {
      const res = await fetchImpl(`${env.identityBaseUrl}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const body = (await res.json()) as RefreshResponse;
      if (!body.accessToken || !body.refreshToken) return null;
      setCustomerToken(body.accessToken);
      setCustomerRefreshToken(body.refreshToken);
      return body.accessToken;
    } catch {
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

export async function requestJson<T>(opts: RequestJsonOptions): Promise<T> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const url = `${opts.baseUrl}${opts.path}`;
  const method = opts.method ?? 'GET';

  const headers: Record<string, string> = {
    accept: 'application/json',
    traceparent: buildTraceparent(),
  };
  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (opts.bearerToken) {
    headers['authorization'] = `Bearer ${opts.bearerToken}`;
  }

  let res: Response;
  try {
    res = await fetchImpl(url, {
      method,
      headers,
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (cause) {
    throw new ApiError(
      `Network error contacting ${url}: ${(cause as Error).message}`,
      0,
    );
  }

  if (res.status === 204) {
    return null as T;
  }

  // 401 → identity-service refresh-and-retry interceptor.
  if (res.status === 401 && opts.suppressAuthRefresh !== true) {
    if (!opts._isRetry) {
      const refreshed = await attemptRefresh(fetchImpl);
      if (refreshed !== null) {
        return requestJson<T>({
          ...opts,
          _isRetry: true,
          bearerToken: refreshed,
        });
      }
    }
    // Refresh exhausted (no token, refresh failed, or retry also 401) — drop
    // both tokens. The next render lands on `/auth/login` via
    // `RequireCustomerAuth`.
    clearCustomerToken();
    clearCustomerRefreshToken();
  }

  let body: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      // non-JSON body — keep it as a string for diagnostics.
      body = text;
    }
  }

  if (!res.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw new ApiError(message, res.status, body);
  }

  return body as T;
}

interface FetchTrackingOptions {
  /** Override the global `fetch`. Used by tests. */
  readonly fetchImpl?: typeof fetch;
  /** AbortSignal forwarded to fetch. */
  readonly signal?: AbortSignal;
}

/** Build a syntactically valid W3C traceparent. We don't have an active
 *  span on the client today (no OTel browser SDK in scope), so we mint a
 *  fresh one per call. The upstream service can adopt it as its parent. */
export function buildTraceparent(): string {
  const traceId = randomHex(32);
  const spanId = randomHex(16);
  // version=00, flags=01 (sampled). Both fixed.
  return `00-${traceId}-${spanId}-01`;
}

function randomHex(length: number): string {
  const bytes = new Uint8Array(length / 2);
  // crypto.getRandomValues is universally available in browsers and jsdom.
  crypto.getRandomValues(bytes);
  let out = '';
  for (const byte of bytes) {
    out += byte.toString(16).padStart(2, '0');
  }
  return out;
}

/**
 * Look up a tracking number against service-mail's public endpoint.
 * Returns `null` on 404 so the caller can render an "unknown number" empty
 * state without catching an exception.
 */
export async function fetchPublicTracking(
  trackingNumber: string,
  options: FetchTrackingOptions = {},
): Promise<PublicMailItemView | null> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const url = `${env.serviceMailBaseUrl}/v1/track/${encodeURIComponent(trackingNumber)}`;

  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        traceparent: buildTraceparent(),
      },
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    // Network-level failure (DNS, offline, CORS preflight reject). Surface
    // a typed error so the UI can distinguish from HTTP errors if needed.
    throw new ApiError(
      `Network error contacting service-mail: ${(cause as Error).message}`,
      0,
    );
  }

  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new ApiError(`service-mail responded ${res.status}`, res.status);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch (cause) {
    throw new ApiError(
      `service-mail returned non-JSON body: ${(cause as Error).message}`,
      res.status,
    );
  }

  if (!isPublicMailItemView(body)) {
    throw new ApiError('service-mail returned malformed PublicMailItemView', res.status);
  }
  return body;
}
